import {
  memoryCacheDelete,
  memoryCacheDeletePrefix,
  memoryCacheKeys,
  memoryCacheSet,
  memoryCacheSize,
  memoryCacheTryGet,
  SAFE_QUERY_MEMORY_TTL_MS,
} from "@/lib/safe-query/memory-cache"

type InflightRecord = {
  promise: Promise<unknown>
  signal: AbortSignal
}

const inflight = new Map<string, InflightRecord>()
const loopHits = new Map<string, number[]>()
/** Monotonic generation per key — stale completions compare against this. */
const keyEpoch = new Map<string, number>()
const globalFetchStarts: number[] = []

type Leader = { controller: AbortController }
const keyLeaders = new Map<string, Leader>()

const IS_DEV = process.env.NODE_ENV === "development"

const GLOBAL_STORM_WINDOW_MS = 500
const GLOBAL_STORM_THRESHOLD = 40

function touchGlobalStormWatch(): void {
  if (!IS_DEV) return
  const t = Date.now()
  const next = globalFetchStarts.filter((x) => t - x < GLOBAL_STORM_WINDOW_MS)
  next.push(t)
  globalFetchStarts.length = 0
  globalFetchStarts.push(...next)
  if (next.length >= GLOBAL_STORM_THRESHOLD) {
    const stack = new Error("[safe-query] global storm stack").stack
    // eslint-disable-next-line no-console -- intentional dev-only guardrail
    console.error("[safe-query] Global request storm suspected", {
      windowMs: GLOBAL_STORM_WINDOW_MS,
      startsInWindow: next.length,
      stack,
    })
  }
}

function nextEpoch(queryKey: string): number {
  const n = (keyEpoch.get(queryKey) ?? 0) + 1
  keyEpoch.set(queryKey, n)
  return n
}

function isJoinableInflight(record: InflightRecord): boolean {
  return !record.signal.aborted
}

function clearInflightEntry(queryKey: string, record: InflightRecord): void {
  if (inflight.get(queryKey) === record) {
    inflight.delete(queryKey)
  }
  const leader = keyLeaders.get(queryKey)
  if (leader?.controller.signal === record.signal) {
    keyLeaders.delete(queryKey)
  }
}

export function abortActiveQueryForKey(queryKey: string): void {
  const L = keyLeaders.get(queryKey)
  if (L) {
    try {
      L.controller.abort()
    } catch {
      /* ignore */
    }
    keyLeaders.delete(queryKey)
  }
}

function touchLoopWatch(queryKey: string, kind: "start" | "join"): void {
  if (!IS_DEV) return
  const t = Date.now()
  const prev = loopHits.get(queryKey) ?? []
  const next = prev.filter((x) => t - x < 2000)
  next.push(t)
  loopHits.set(queryKey, next)
  if (next.length > 3) {
    const windowStart = Math.min(...next)
    const windowEnd = Math.max(...next)
    const stack = new Error("[safe-query] loop detection stack").stack
    // eslint-disable-next-line no-console -- intentional dev-only guardrail
    console.error("[safe-query] Potential API loop detected", {
      queryKey,
      kind,
      windowMs: 2000,
      windowStart,
      windowEnd,
      hits: next.length,
      timestamps: next,
      stack,
    })
  }
}

export type SafeQueryCacheMode = "memory" | "none"

export type MemoryWriteDecision = { write: boolean; ttlMs?: number }

export type RunSafeQueryOptions<T = unknown> = {
  force?: boolean
  signal?: AbortSignal
  cache?: SafeQueryCacheMode
  ttlMs?: number
  /**
   * Override default memory persistence (`undefined` → do not write; otherwise use default TTL).
   * Default: write only when `result !== undefined`.
   */
  memoryWrite?: (result: T) => MemoryWriteDecision
}

/** Default persistence: skip `undefined` / `null`; use hook or `ttlMs` for TTL. */
export function defaultSafeQueryMemoryWrite<T>(result: T): MemoryWriteDecision {
  return { write: result !== undefined && result !== null, ttlMs: undefined }
}

function linkAbort(parent: AbortSignal | undefined, child: AbortController): void {
  if (!parent) return
  if (parent.aborted) {
    child.abort()
    return
  }
  parent.addEventListener("abort", () => child.abort(), { once: true })
}

function createAbortError(message: string): Error {
  if (typeof DOMException !== "undefined") {
    try {
      return new DOMException(message, "AbortError")
    } catch {
      /* fall through — some runtimes disallow constructing DOMException */
    }
  }
  const err = new Error(message)
  err.name = "AbortError"
  return err
}

/**
 * Single-flight fetch with optional TTL memory cache, per-key leader abort, epoch stale guard, and LRU eviction.
 */
export async function runSafeQuery<T>(
  queryKey: string,
  fetcher: (signal: AbortSignal) => Promise<T>,
  options?: RunSafeQueryOptions<T>
): Promise<T> {
  installSafeQueryAbortRejectionGuard()

  const force = Boolean(options?.force)
  const outer = options?.signal
  const cache = options?.cache ?? "memory"
  const ttlMs = options?.ttlMs ?? SAFE_QUERY_MEMORY_TTL_MS
  const memWrite = options?.memoryWrite ?? defaultSafeQueryMemoryWrite

  if (!queryKey) {
    throw new Error("runSafeQuery: empty queryKey")
  }

  if (cache === "memory" && !force) {
    const hit = memoryCacheTryGet(queryKey)
    if (hit.hit) {
      return Promise.resolve(hit.value as T)
    }
  }

  const existing = inflight.get(queryKey)
  if (existing && !force) {
    if (isJoinableInflight(existing)) {
      touchLoopWatch(queryKey, "join")
      return existing.promise as Promise<T>
    }
    clearInflightEntry(queryKey, existing)
  }

  if (force) {
    memoryCacheDelete(queryKey)
  }

  abortActiveQueryForKey(queryKey)

  const myEpoch = nextEpoch(queryKey)

  const inner = new AbortController()
  keyLeaders.set(queryKey, { controller: inner })
  linkAbort(outer, inner)

  touchLoopWatch(queryKey, "start")
  touchGlobalStormWatch()

  const record: InflightRecord = {
    promise: undefined as unknown as Promise<unknown>,
    signal: inner.signal,
  }

  const p = (async (): Promise<T> => {
    try {
      const result = await fetcher(inner.signal)
      if (myEpoch !== keyEpoch.get(queryKey)) {
        // A newer generation owns this key — prefer joining it over rejecting, so
        // forgotten `await`/`void` callers do not surface CRA "Uncaught runtime errors".
        const newer = inflight.get(queryKey)
        if (newer && newer.promise !== record.promise && isJoinableInflight(newer)) {
          return (await newer.promise) as T
        }
        throw createAbortError("safe-query: stale response discarded")
      }
      if (!inner.signal.aborted && cache === "memory") {
        const decision = memWrite(result)
        if (decision.write) {
          memoryCacheSet(queryKey, result, decision.ttlMs ?? ttlMs)
        }
      }
      return result
    } finally {
      clearInflightEntry(queryKey, record)
    }
  })()

  record.promise = p
  inflight.set(queryKey, record)
  return p
}

export function invalidateQuery(queryKey: string): void {
  memoryCacheDelete(queryKey)
  nextEpoch(queryKey)
  abortActiveQueryForKey(queryKey)
  const existing = inflight.get(queryKey)
  if (existing) {
    inflight.delete(queryKey)
  }
}

export function invalidateQueryPrefix(prefix: string): void {
  const keys = new Set<string>()
  for (const k of memoryCacheKeys()) {
    if (k.startsWith(prefix)) keys.add(k)
  }
  for (const k of inflight.keys()) {
    if (k.startsWith(prefix)) keys.add(k)
  }
  for (const k of keyLeaders.keys()) {
    if (k.startsWith(prefix)) keys.add(k)
  }
  memoryCacheDeletePrefix(prefix)
  for (const k of keys) {
    nextEpoch(k)
    abortActiveQueryForKey(k)
    const ip = inflight.get(k)
    if (ip) inflight.delete(k)
  }
}

export function isAbortLikeError(e: unknown): boolean {
  if (e == null || typeof e !== "object") return false
  const err = e as { name?: string; code?: string; message?: string }
  if (err.name === "AbortError" || err.name === "CanceledError" || err.code === "ERR_CANCELED") return true
  if (err.message === "safe-query: stale response discarded") return true
  return false
}

let abortRejectionGuardInstalled = false

/**
 * Dev safety net: intentional safe-query abort/stale races must not trip CRA's overlay
 * when a caller forgot `try/catch` / `.catch`. Idempotent; browser-only.
 */
export function installSafeQueryAbortRejectionGuard(): void {
  if (abortRejectionGuardInstalled) return
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") return
  abortRejectionGuardInstalled = true
  window.addEventListener("unhandledrejection", (event) => {
    if (isAbortLikeError(event.reason)) {
      event.preventDefault()
    }
  })
}

/** Dev / tests: read-only snapshot of engine state (no secrets). */
export function getSafeQueryObservabilitySnapshot(): {
  memoryEntryCount: number
  memoryKeys: string[]
  inflightCount: number
  inflightKeys: string[]
  leaderKeyCount: number
  leaderKeys: string[]
} {
  return {
    memoryEntryCount: memoryCacheSize(),
    memoryKeys: memoryCacheKeys(),
    inflightCount: inflight.size,
    inflightKeys: [...inflight.keys()],
    leaderKeyCount: keyLeaders.size,
    leaderKeys: [...keyLeaders.keys()],
  }
}

/**
 * Jest / diagnostics: clears memory cache, inflight, leaders, epochs, and dev counters.
 * Do not use in production UI.
 */
export function resetSafeQueryEngineForTests(): void {
  for (const k of [...keyLeaders.keys()]) {
    abortActiveQueryForKey(k)
  }
  inflight.clear()
  memoryCacheDeletePrefix("")
  keyEpoch.clear()
  loopHits.clear()
  globalFetchStarts.length = 0
}
